import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";
import useApi from "../../hooks/useApi";
import useValidateColaborador from "../../hooks/useValidateColaborador";

const LICENCAS_ENDPOINT = "/Painel/API/InventarioTI/GetLicencasColaborador/";

const SectionTitle = styled.p`
  margin: 4px 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #263b4b;
`;

const InfoBox = styled.div`
  background: #f2f5ff;
  border-left: 4px solid #004aad;
  padding: 12px 14px;
  border-radius: 6px;
  font-size: 13px;
  color: #263b4b;
  line-height: 1.5;
  margin-top: 4px;
`;

const WarningBox = styled.div`
  background: #fef2f2;
  border-left: 4px solid #b91c1c;
  padding: 14px 16px;
  border-radius: 6px;
  font-size: 13px;
  color: #7f1d1d;
  line-height: 1.5;
  margin-top: 4px;

  strong {
    color: #7f1d1d;
  }
`;

const ListShell = styled.div`
  margin-top: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 4px;
  }
`;

const ListHeaderEquip = styled.div`
  position: sticky;
  top: 0;
  display: grid;
  grid-template-columns: 70px 1fr 120px 120px;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #475569;
  text-transform: uppercase;
  z-index: 1;
`;

const ListHeaderLicenca = styled.div`
  position: sticky;
  top: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #475569;
  text-transform: uppercase;
  z-index: 1;
`;

const EquipRow = styled.div`
  display: grid;
  grid-template-columns: 70px 1fr 120px 120px;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  font-size: 13px;
  color: #263b4b;

  &:last-child {
    border-bottom: none;
  }
`;

const LicencaRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  font-size: 13px;
  color: #263b4b;

  &:last-child {
    border-bottom: none;
  }
`;

const EquipCell = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyRow = styled.div`
  padding: 18px 12px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;
`;

const SecondaryButton = styled.button`
  all: unset;
  cursor: pointer;
  flex: 1;
  text-align: center;
  padding: 12px 0;
  border-radius: 8px;
  background: #e2e8f0;
  color: #263b4b;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;

  &:hover {
    background: #cbd5e1;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  all: unset;
  cursor: pointer;
  flex: 1;
  text-align: center;
  padding: 12px 0;
  border-radius: 8px;
  background: #b91c1c;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;

  &:hover {
    background: #991b1b;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default function ModalOffboardColaborador({
  onClose,
  equipamentos,
  onOffboarded,
}) {
  const api = useApi();

  const [step, setStep] = useState("select");
  const [matriculaInput, setMatriculaInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [licencasVinculadas, setLicencasVinculadas] = useState([]);
  const [loadingLicencas, setLoadingLicencas] = useState(false);

  const matriculaSelecionada = matriculaInput.trim();

  const {
    info: colabInfo,
    validating,
    error: validationError,
  } = useValidateColaborador(matriculaSelecionada, { bloquearDemitido: false });

  const equipamentosVinculados = useMemo(() => {
    if (!matriculaSelecionada) return [];
    return (equipamentos ?? []).filter(
      (e) => String(e.responsavel).trim() === matriculaSelecionada
    );
  }, [equipamentos, matriculaSelecionada]);

  useEffect(() => {
    if (!matriculaSelecionada || matriculaSelecionada.length !== 6) {
      setLicencasVinculadas([]);
      return;
    }

    let cancelled = false;

    const carregarLicencas = async () => {
      setLoadingLicencas(true);
      try {
        const { data } = await api.get(LICENCAS_ENDPOINT, {
          params: { matricula: matriculaSelecionada },
        });
        if (!cancelled) {
          setLicencasVinculadas(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setLicencasVinculadas([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingLicencas(false);
        }
      }
    };

    carregarLicencas();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useApi retorna nova instância a cada render
  }, [matriculaSelecionada]);

  const matriculaValida =
    matriculaSelecionada.length === 6 &&
    !validating &&
    !validationError &&
    Boolean(colabInfo);

  const totalEquipamentos = equipamentosVinculados.length;
  const totalLicencas = licencasVinculadas.length;
  const temPendencias = totalEquipamentos > 0 || totalLicencas > 0;
  const totalPendencias = totalEquipamentos + totalLicencas;

  const avancarParaConfirmacao = (e) => {
    e.preventDefault();
    if (!matriculaValida || !temPendencias || loadingLicencas) return;
    setStep("confirm");
  };

  const confirmarOffboard = async () => {
    if (!matriculaValida || !temPendencias) return;
    try {
      setSaving(true);
      const { data } = await api.post("/Painel/API/InventarioTI/OffboardColaborador/", {
        matricula: matriculaSelecionada,
      });
      alert(
        data?.message ||
          `Offboard realizado. ${data?.total_equipamentos ?? 0} equipamento(s) e ${data?.total_licencas ?? 0} licença(s) encerrados.`
      );
      onClose?.();
      onOffboarded?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao realizar offboard.");
    } finally {
      setSaving(false);
    }
  };

  const renderResumoPendencias = () => {
    const partes = [];
    if (totalEquipamentos > 0) {
      partes.push(
        <>
          <strong>{totalEquipamentos}</strong> equipamento
          {totalEquipamentos === 1 ? "" : "s"}
        </>
      );
    }
    if (totalLicencas > 0) {
      partes.push(
        <>
          <strong>{totalLicencas}</strong> licença
          {totalLicencas === 1 ? "" : "s"}
        </>
      );
    }

    return partes.reduce((acc, item, index) => {
      if (index === 0) return [item];
      if (index === partes.length - 1) return [...acc, " e ", item];
      return [...acc, ", ", item];
    }, []);
  };

  if (step === "select") {
    const semVinculos = matriculaValida && !loadingLicencas && !temPendencias;

    return (
      <Overlay state={true}>
        <Modal header close={onClose} size="md">
          <Formulario onSubmit={avancarParaConfirmacao}>
            <FormComp
              title="MATRÍCULA DO COLABORADOR"
              type="numberOnly"
              placeholder="EX: 012345"
              valor={matriculaInput}
              name="matricula"
              changeFunc={(e) =>
                setMatriculaInput(e.target.value.replace(/\D/g, ""))
              }
              required
              maxLength={6}
            />
            <FormComp
              title="NOME"
              type="text"
              readOnly
              valor={colabInfo?.nome || ""}
              placeholder={validating ? "Validando matrícula..." : ""}
            />
            <FormComp
              title="FUNÇÃO"
              type="text"
              readOnly
              valor={colabInfo?.funcao || ""}
            />
            <FormComp
              title="CENTRO DE CUSTO"
              type="text"
              readOnly
              valor={
                colabInfo?.centro_custo_desc || colabInfo?.centro_custo || ""
              }
            />
            {validationError && (
              <p
                style={{ color: "#B91C1C", fontSize: "12px", marginTop: "-6px" }}
              >
                {validationError}
              </p>
            )}

            {matriculaValida && loadingLicencas && (
              <InfoBox>Carregando licenças ativas do colaborador...</InfoBox>
            )}

            {matriculaValida && !loadingLicencas && temPendencias && (
              <div>
                <SectionTitle>RESUMO</SectionTitle>
                <InfoBox>
                  Serão encerrados {renderResumoPendencias()} vinculados à
                  matrícula <strong>{matriculaSelecionada}</strong>. Clique em
                  AVANÇAR para revisar e confirmar.
                </InfoBox>
              </div>
            )}

            {semVinculos && (
              <div>
                <SectionTitle>RESUMO</SectionTitle>
                <WarningBox>
                  Nenhum equipamento ou licença ativa vinculada à matrícula{" "}
                  <strong>{matriculaSelecionada}</strong>.
                </WarningBox>
              </div>
            )}

            <FormComp
              type="submit"
              valor={
                validating || loadingLicencas ? "VALIDANDO..." : "AVANÇAR"
              }
              disabled={
                !matriculaValida ||
                validating ||
                loadingLicencas ||
                !temPendencias
              }
            />
          </Formulario>
        </Modal>
      </Overlay>
    );
  }

  return (
    <Overlay state={true}>
      <Modal header close={saving ? undefined : onClose} size="lg">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SectionTitle>CONFIRMAÇÃO DE OFFBOARDING</SectionTitle>

          <WarningBox>
            Atenção: ao confirmar, os vínculos abaixo serão encerrados para a
            matrícula <strong>{matriculaSelecionada}</strong>
            {colabInfo?.nome ? (
              <>
                {" "}
                (<strong>{colabInfo.nome}</strong>)
              </>
            ) : null}
            . Equipamentos serão desvinculados e marcados como disponíveis; as
            licenças ativas serão encerradas. Esta ação não pode ser desfeita
            automaticamente.
          </WarningBox>

          {totalEquipamentos > 0 && (
            <div>
              <SectionTitle>
                EQUIPAMENTOS A SEREM REMOVIDOS ({totalEquipamentos})
              </SectionTitle>
              <ListShell>
                <ListHeaderEquip>
                  <span>ID</span>
                  <span>Equipamento</span>
                  <span>Patrimônio</span>
                  <span>Nº Série</span>
                </ListHeaderEquip>

                {equipamentosVinculados.map((e) => (
                  <EquipRow key={e.id}>
                    <EquipCell title={e.id}>{e.id}</EquipCell>
                    <EquipCell title={e.descricao}>
                      {e.tipo !== "—" ? `${e.tipo} — ` : ""}
                      {e.descricao}
                    </EquipCell>
                    <EquipCell title={e.patrimonio}>{e.patrimonio}</EquipCell>
                    <EquipCell title={e.serial}>{e.serial}</EquipCell>
                  </EquipRow>
                ))}
              </ListShell>
            </div>
          )}

          {totalLicencas > 0 && (
            <div>
              <SectionTitle>
                LICENÇAS A SEREM ENCERRADAS ({totalLicencas})
              </SectionTitle>
              <ListShell>
                <ListHeaderLicenca>
                  <span>Fornecedor</span>
                  <span>Licença</span>
                </ListHeaderLicenca>

                {licencasVinculadas.map((lic) => (
                  <LicencaRow key={lic.id}>
                    <EquipCell title={lic.fornecedor}>
                      {lic.fornecedor || "—"}
                    </EquipCell>
                    <EquipCell title={lic.tipo || lic.licenca}>
                      {lic.tipo || lic.licenca || "—"}
                    </EquipCell>
                  </LicencaRow>
                ))}
              </ListShell>
            </div>
          )}

          <ButtonRow>
            <SecondaryButton
              type="button"
              onClick={() => setStep("select")}
              disabled={saving}
            >
              VOLTAR
            </SecondaryButton>
            <DangerButton
              type="button"
              onClick={confirmarOffboard}
              disabled={saving || !temPendencias}
            >
              {saving
                ? "ENCERRANDO..."
                : `CONFIRMAR OFFBOARDING (${totalPendencias})`}
            </DangerButton>
          </ButtonRow>
        </div>
      </Modal>
    </Overlay>
  );
}
